import {
	Timeline,
	TimelineConnector,
	TimelineContent,
	TimelineDot,
	TimelineItem,
	TimelineOppositeContent,
	TimelineSeparator,
} from "@mui/lab";
import React, { useEffect, useRef, useState } from "react";
import { MenuItem, MenuList } from "@mui/material";
import { useDispatch } from "react-redux";
import { ApiService, DocumentService, ExportService } from "../../../../../../api";
import {
	CircularLoader,
	createNotification,
	CustomButton,
	CustomMenuButton,
	CustomTooltip,
	HorizontalDivider,
	icon,
	IconComponent,
	SidePanelContainer,
} from "../../../../../../common/components";
import { useApi, useTrackEvent } from "../../../../../../common/hooks";
import {
	hasPermission,
	isSegFeatureEnabled,
	SegFlags,
	translate,
	translateDate,
} from "../../../../../../common/providers";
import permissions from "../../../../../../common/providers/permissions/permissions";
import { downloadFile, getIconFromFiletype } from "../../../../../../common/utils";
import styles from "./DocumentCenterVersionTab.module.css";
import DocumentCenterVersionDialog from "./DocumentCenterVersionDialog";
import projectDocumentService from "../../../../../../api/services/project/project-document-service";
import { setRefreshSelectedDocument } from "../../../../slice/document-center-slice";

export default function DocumentCenterVersionTab({ id: docId, isOngoing, label, type, onAddVersion, phaseLocked }) {
	const { call: getVersions, cancel, isLoading } = useApi(DocumentService.getVersions);
	const { call: associateNewVersionOfDoc } = useApi(projectDocumentService.associateNewVersionOfDoc);
	const { call: checkDocumentForVersioning } = useApi(projectDocumentService.checkDocumentForVersioning);
	const [versions, setVersions] = useState([]);
	const [openDialog, setOpenDialog] = useState(false);
	const [selectedDoc, setSelectedDoc] = useState(null);
	const [isValidForVersioning, setIsValidForVersioning] = useState(null);
	const cancelTokenSourceRef = useRef(null);
	const menuButtonRef = useRef(null);
	const menuListRef = useRef(null);
	const trackEvent = useTrackEvent();
	const dispatch = useDispatch();

	useEffect(() => {
		if (!docId) {
			return undefined;
		}
		if (isSegFeatureEnabled(SegFlags.VERSIONS)) {
			getVersions({ docId }).then(setVersions).catch(console.error);
		}
		return () => {
			cancel();
		};
	}, [docId, getVersions, cancel]);
	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);
	useEffect(() => {
		const handleCloseMenuList = (event) => {
			if (menuListRef.current && !menuListRef.current.contains(event.target)) {
				menuButtonRef.current?.onClose?.();
			}
		};

		document.addEventListener("mousedown", handleCloseMenuList);
		return () => {
			document.removeEventListener("mousedown", handleCloseMenuList);
		};
	}, []);
	const handleCloseMenu = () => {
		if (menuButtonRef.current?.onClose) {
			menuButtonRef.current.onClose?.();
		}
	};
	const handleExport = (documentId) => {
		createNotification({ message: translate("document-center.side-panel.versions.notif-exporting") });
		ExportService.exportVersioning({ docId: documentId }, cancelTokenSourceRef.current.token)
			.then(({ data, filename }) => downloadFile({ data, filename, filetype: "xlsx" }))
			.catch((err) => {
				console.error(err);
			});
	};
	const handleResetValidation = () => {
		setIsValidForVersioning(null);
	};
	const handleOpenDialog = () => {
		setOpenDialog((prev) => !prev);
		if (!isValidForVersioning) {
			setIsValidForVersioning(false);
			handleResetValidation();
		}
		handleCloseMenu();
	};
	const handleCheckDocumentForVersioning = (doc) => {
		checkDocumentForVersioning({ newDocId: doc?.id })
			.then((data) => {
				setIsValidForVersioning(data.value);
				setSelectedDoc(doc);
			})
			.catch((err) => {
				console.error(err);
			});
	};
	const handleValidateVersioning = () => {
		createNotification({
			type: "success",
			message: translate("document-center.side-panel.versions.version-comparison.initialization"),
		});
		associateNewVersionOfDoc({ newDocId: selectedDoc?.id, oldDocId: docId })
			.then(() => {
				handleOpenDialog();
				trackEvent("Document_Manual_V2_Correspondance");
			})
			.catch((err) => {
				console.error(err);
			})
			.finally(() => {
				dispatch(setRefreshSelectedDocument(true));
			});
	};
	return (
		<>
			<SidePanelContainer className={styles.tab}>
				<div className={styles.tab__header}>
					<span className={styles.tab__title}>{translate("document-center.side-panel.versions")}</span>
					{isSegFeatureEnabled(SegFlags.VERSIONS) &&
						hasPermission(permissions.PROJECT_LEADER, permissions.PROJECT_MANAGER) && (
							<CustomMenuButton
								ref={menuButtonRef}
								btnClassName={styles.btnIcon}
								color="primary"
								disabled={phaseLocked}
								endIcon={icon.faChevronDown}
								text={translate("document-center.side-panel.versions.add")}
								variant="outlined"
							>
								<MenuList ref={menuListRef} className={styles.menuList}>
									<CustomTooltip
										title={
											!isSegFeatureEnabled(SegFlags.DOCUMENT_CENTER_WRITING) &&
											translate("document-center.feature-right.update.disabled")
										}
									>
										<span>
											<MenuItem
												className={styles.menuItem}
												disabled={!isSegFeatureEnabled(SegFlags.DOCUMENT_CENTER_WRITING)}
											>
												<span className={styles.menuItem__text}>
													<input
														hidden
														accept=".doc,.docx,.odt,.pdf,.rtf,.xls,.xlsm,.xlsx,.zip"
														disabled={
															isOngoing ||
															!isSegFeatureEnabled(SegFlags.DOCUMENT_CENTER_WRITING)
														}
														id="importVersion"
														name="importVersion"
														type="file"
														onChange={onAddVersion}
													/>
													<label htmlFor="importVersion">
														<span className={styles.menuItem__text}>
															{translate(
																"document-center.side-panel.versions.add-from-system"
															)}
														</span>
													</label>
												</span>
											</MenuItem>
										</span>
									</CustomTooltip>
									<HorizontalDivider />
									<MenuItem className={styles.menuItem} onClick={handleOpenDialog}>
										<span className={styles.menuItem__text}>
											{translate("document-center.side-panel.versions.add-from-platform")}
										</span>
									</MenuItem>
								</MenuList>
							</CustomMenuButton>
						)}
				</div>
				{isLoading && (
					<div className={styles.tab__content}>
						<CircularLoader />
					</div>
				)}
				{!isSegFeatureEnabled(SegFlags.VERSIONS) && (
					<div className={styles.noContent}>
						<IconComponent
							className={styles.noContent__infoIcon}
							color="var(--blue-500)"
							icon={icon.faInfoCircle}
							size="sm"
						/>
						<div className={styles.noContent__desc}>
							{translate("document-center.side-panel.versions.no-content.description")}
						</div>
					</div>
				)}
				{!isLoading && (
					<Timeline align="left" className={styles.timeline}>
						{Array.isArray(versions) &&
							versions.map((version, index) => (
								<TimelineItem key={version.id}>
									<TimelineOppositeContent>
										<span
											className={`${styles.timeline_date} ${
												(index === 0 && styles["timeline_date--current"]) || ""
											}`}
										>
											{translateDate(version.uploaded)}
										</span>
									</TimelineOppositeContent>
									<TimelineSeparator>
										<TimelineDot className={styles.timeline__dot} />
										<TimelineConnector className={styles.timeline__connector} />
									</TimelineSeparator>
									<TimelineContent>
										<span className={styles.content__main}>
											<IconComponent
												color="var(--grey-400)"
												icon={icon[getIconFromFiletype(type)]}
												size="md"
											/>
											<span
												className={`${styles.content__name} ${
													(index === 0 && styles["content__name--current"]) || ""
												}`}
											>
												{version.name}
											</span>
										</span>
										<span className={styles.content__filename}>{version.filename}</span>
										{versions.length > 1 && index < versions.length - 1 && (
											<CustomButton
												className={styles.button}
												color="secondary"
												iconColor="var(--grey-600)"
												startIcon={icon.faFileExport}
												variant="outlined"
												onClick={() => handleExport(version.id)}
											>
												{translate("document-center.side-panel.versions.export")}
											</CustomButton>
										)}
									</TimelineContent>
								</TimelineItem>
							))}
					</Timeline>
				)}
			</SidePanelContainer>
			<DocumentCenterVersionDialog
				hasSearch=""
				isValid={isValidForVersioning}
				open={openDialog}
				selectedDocId={docId}
				selectedDocumentLabel={label}
				onChange={handleCheckDocumentForVersioning}
				onCloseDialog={handleOpenDialog}
				onResetValidation={handleResetValidation}
				onValidateNewDocument={handleValidateVersioning}
			/>
		</>
	);
}
